'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/ml-extra'

const KNNViz = dynamic(() => import('../_components/widgets/MLExtraWidgets').then(m => m.KNNViz), { ssr: false })

export default function NearestNeighbors() {
  return (
    <Lesson>
      <Step>
        <H>Learning without training</H>
        <P>
          Here’s a completely different way to learn. Don’t fit any weights. Just remember every example, and when a new point
          arrives, look up the <strong>k</strong> most similar examples you’ve seen and let them vote. That’s{' '}
          <strong>k-nearest neighbours</strong>, or kNN.
        </P>
        <Figure n="6.8" wide caption="Drag the ? to ask its neighbours. Then slide k: with k = 1 the regions hug every point (overfitting); with a huge k they flatten out (underfitting).">
          <KNNViz />
        </Figure>
      </Step>

      <Step gate={['q-k', 'knn-basics']}>
        <H>Choosing k</H>
        <Quiz
          id="q-k"
          question="With k = 1, what is the training accuracy of kNN (when no two identical points have different labels)?"
          options={[
            { label: '100% — every training point’s nearest neighbour is itself', correct: true },
            { label: 'Around 50%', why: 'When you ask about a training point, its closest neighbour is the point itself, with the right label.' },
            { label: 'It depends on the learning rate', why: 'kNN has no learning rate. It doesn’t train at all.' },
          ]}
          explain="Perfect training accuracy, the classic overfitting warning. k is a hyperparameter: pick it using validation data, never training data."
        />
        <Exercise
          id="knn-basics"
          title="distance and knnPredict"
          {...EX.knnBasics}
          hints={[
            <>
              <C>{'let s = 0; for (let i = 0; i < a.length; i++) s += (a[i] - b[i]) ** 2; return Math.sqrt(s)'}</C>
            </>,
            <>
              <C>{'range(trainX.length).sort((i, j) => distance(trainX[i], x) - distance(trainX[j], x)).slice(0, k)'}</C> gives
              the nearest indices. Count their labels in a Map and return the most common.
            </>,
          ]}
        />
      </Step>

      <Step gate="knn-digits">
        <H>Handwriting, by lookup</H>
        <P>
          Nearest neighbour works on anything you can measure a distance between, including images. Each 14 × 14 digit is a
          point in 196-dimensional space. Find the closest stored image, and copy its label.
        </P>
        <Exercise id="knn-digits" timeoutMs={60000} title="1-nearest-neighbour digits" {...EX.knnDigits} hints={[<>Keep the index with the smallest <C>distance2(trainX[i], x)</C> and return its label.</>]} />
        <Callout kind="plain">
          It beats the linear model from the multiclass lesson, with no training at all. The catch: to classify one image it
          compares against <em>every</em> stored example. With millions of examples, that gets slow and memory-hungry.
        </Callout>
      </Step>

      <Step gate="q-scale-knn">
        <H>Distance is everything</H>
        <P>
          kNN is only as good as its idea of “close”. Two pitfalls matter a lot:
        </P>
        <UL>
          <li>
            <strong>Scale.</strong> If one feature is in square metres (hundreds) and another in bedrooms (single digits), the square metres
            completely dominate the distance. Standardise first.
          </li>
          <li>
            <strong>Too many dimensions.</strong> In very high-dimensional spaces, everything ends up roughly the same distance
            from everything else. This is called the <em>curse of dimensionality</em>.
          </li>
        </UL>
        <Quiz
          id="q-scale-knn"
          question="Houses described by [size in m², number of bathrooms]. Without standardising, a 2-bathroom difference counts as much as…"
          options={[
            { label: 'a 2 m² difference in size — almost nothing', correct: true },
            { label: 'a 200 m² difference in size', why: 'Raw distance just subtracts the numbers. 2 bathrooms counts the same as 2 m².' },
            { label: 'nothing at all', why: 'It still counts — just very little next to the size differences.' },
          ]}
        />
        <Callout kind="why">
          “Find the nearest stored examples” is a big idea in modern AI. Retrieval-augmented generation (Module 10) gives a
          language model the nearest documents to your question, measured by the distance between embeddings.
        </Callout>
      </Step>
    </Lesson>
  )
}
